/**
 * Gravity blocks: sand, red sand, gravel, anvils and the dragon egg, like
 * vanilla's FallingBlock and FallingBlockEntity.
 *
 * When a gravity block is placed, or the cell below one changes, a check is
 * scheduled two ticks out. If the cell below is free then (air, fluid, fire, a
 * replaceable plant) the block is removed and a falling-block entity carrying
 * its id takes over: 0.04 blocks/tick^2 gravity, 0.98 drag, no sideways motion,
 * a 0.98 x 0.98 box sweeping through the blocks' real collision shapes (so it
 * drops through grass, water and a torch's empty cell alike). A column therefore
 * falls one block at a time from the bottom up: each block leaves two ticks
 * after the one below it did.
 *
 * Where it stops, the cell it is in decides: replaceable (air, fluid, plants,
 * a snow layer) and with something to rest on, it becomes the block again;
 * otherwise (a torch's cell, a bottom slab's, a rail's) it breaks and drops as
 * an item. After 600 ticks in the air, or 100 and out of the world, it drops
 * too. An anvil hurts what it lands on, `2 * (blocks fallen - 1)` up to 40, and
 * may chip or break doing so (5% + 5% per block).
 *
 * DOM-free and host-agnostic: the offline game (main.ts) and the game server
 * (server/serverWorld.ts, server/actions/falling.ts) drive the same class.
 * Not done: concrete powder (no such block here), pointed dripstone, bubble
 * columns, and falling blocks do not get slowed by cobwebs or honey.
 */

import {
  AIR, ANVIL_FAMILIES, anvilState, collisionOf, DEAD_BUSH_ID, FERN_ID, FIRE_ID, IS_GRAVITY, IS_LAVA, IS_WATERY,
  NO_BOXES, SEAGRASS_ID, SNOW_LAYER_ID, SOUL_FIRE_ID, TALL_GRASS_ID,
} from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { clipMove, type ShapesAt } from './collide';

/** Ticks between a block change and the check it triggers (vanilla: scheduleTick(pos, block, 2)). */
export const FALL_DELAY = 2;
/** Entity physics, vanilla's numbers. */
export const GRAVITY = 0.04;
export const DRAG = 0.98;
/** Falling-block hitbox: 0.98 wide and tall, origin at the bottom centre. */
export const HALF_W = 0.49;
export const HEIGHT = 0.98;
/** Ticks in the air after which a falling block gives up (vanilla: time > 600). */
export const MAX_AGE = 600;
/** ... or after this many while at / below the bottom of the world or above its top. */
export const OUT_OF_WORLD_AGE = 100;
/** Anvil: damage per block fallen beyond the first, and the cap (vanilla: setHurtsEntities(2.0F, 40)). */
export const ANVIL_DAMAGE_PER_BLOCK = 2;
export const ANVIL_DAMAGE_MAX = 40;
const EPS = 1e-4;

/** A block in flight: position is the bottom centre of its 0.98 cube. */
export interface FallingBlock {
  /** Block id it carries (an anvil keeps its facing and damage state). */
  id: number;
  x: number;
  y: number;
  z: number;
  /** The place one tick ago, for drawing between ticks. */
  px: number;
  py: number;
  pz: number;
  vy: number;
  /** Ticks in flight. */
  age: number;
  /** Blocks fallen so far. */
  fall: number;
}

export type FallingFx = 'land' | 'break' | 'anvil_land' | 'anvil_break';

export interface FallingHost {
  getBlock(x: number, y: number, z: number): number;
  /** Is the cell inside the world and in a chunk that can be edited right now? */
  canEdit(x: number, y: number, z: number): boolean;
  /** Must report the change back through `FallingSim.blockChanged`. */
  setBlock(x: number, y: number, z: number, id: number): void;
  /** A block broke into an item (id is the item form: the damage state of an anvil, facing 0). */
  drop?: (id: number, x: number, y: number, z: number) => void;
  /** Hurt everything inside the box (a landing anvil). */
  hurt?: (box: readonly number[], amount: number, id: number) => void;
  /** Sound and particles. */
  fx?: (kind: FallingFx, x: number, y: number, z: number, id: number) => void;
  rnd?: () => number;
}

/** Plants and covers a falling block simply replaces (vanilla's `replaceable`). */
const REPLACEABLE_PLANTS = new Set<number>([TALL_GRASS_ID, FERN_ID, DEAD_BUSH_ID, SEAGRASS_ID, SNOW_LAYER_ID, FIRE_ID, SOUL_FIRE_ID]);

/** Can a gravity block pass through / land in this block (vanilla `canBeReplaced`)? */
export function isReplaceable(id: number): boolean {
  return id === AIR || IS_WATERY[id] === 1 || IS_LAVA[id] === 1 || REPLACEABLE_PLANTS.has(id);
}

/** The block below a gravity block lets it drop (vanilla FallingBlock.isFree). */
export const isFree = isReplaceable;

/** The item a broken falling block drops: an anvil comes back as the placeable form of its damage state. */
export function dropIdOf(id: number): number {
  const a = anvilState(id);
  return a ? ANVIL_FAMILIES[a[0]][0] : id;
}

/** Damage a falling anvil deals after `fall` blocks (0 when it fell a block or less). */
export function anvilDamage(fall: number): number {
  const n = Math.ceil(fall - 1 - EPS);
  return n < 0 ? 0 : Math.min(Math.floor(n * ANVIL_DAMAGE_PER_BLOCK), ANVIL_DAMAGE_MAX);
}

interface Check { x: number; y: number; z: number; at: number }

const cellKey = (x: number, y: number, z: number): number => ((x + 0x100000) * 0x200000 + (z + 0x100000)) * 1024 + y;

export class FallingSim {
  /** Blocks in flight, oldest first. */
  readonly blocks: FallingBlock[] = [];
  /** Checks waiting for their tick, by cell. */
  private readonly due = new Map<number, Check>();
  private now = 0;

  constructor(readonly host: FallingHost) {}

  get pending(): number { return this.blocks.length; }

  /** A block changed: a gravity block there, or the one above it, may now have nothing under it. */
  blockChanged(x: number, y: number, z: number): void {
    const h = this.host;
    if (y >= 0 && y < WORLD_HEIGHT && IS_GRAVITY[h.getBlock(x, y, z)] === 1) this.schedule(x, y, z);
    if (y + 1 >= 0 && y + 1 < WORLD_HEIGHT && IS_GRAVITY[h.getBlock(x, y + 1, z)] === 1) this.schedule(x, y + 1, z);
  }

  /** Look at (x, y, z) in FALL_DELAY ticks (an earlier request for the cell stands). */
  schedule(x: number, y: number, z: number): void {
    const k = cellKey(x, y, z);
    if (!this.due.has(k)) this.due.set(k, { x, y, z, at: this.now + FALL_DELAY });
  }

  /** One 20 Hz game tick: run the checks that are due, then fly everything that was already in the air. */
  tick(): void {
    this.now++;
    const flying = this.blocks.length;
    if (this.due.size) {
      for (const [k, c] of this.due) {
        if (c.at > this.now) continue;
        if (!this.host.canEdit(c.x, c.y, c.z)) { c.at = this.now + FALL_DELAY; continue; } // the chunk is not here yet: ask again
        this.due.delete(k);
        this.check(c.x, c.y, c.z);
      }
    }
    // oldest first, like vanilla: a block that lands makes the cell under the next one solid
    let keep = 0;
    for (let i = 0; i < flying; i++) {
      const b = this.blocks[i];
      // frozen while its chunk is not loaded, like every other entity
      const done = this.host.canEdit(Math.floor(b.x), 0, Math.floor(b.z)) && this.fly(b);
      if (!done) this.blocks[keep++] = b;
    }
    if (keep < flying) this.blocks.splice(keep, flying - keep);
  }

  /** The scheduled tick of a gravity block: hand it to an entity when nothing holds it up. */
  private check(x: number, y: number, z: number): void {
    const h = this.host;
    const id = h.getBlock(x, y, z);
    if (IS_GRAVITY[id] !== 1 || y < 0) return;
    if (y > 0 && !isFree(h.getBlock(x, y - 1, z))) return;
    h.setBlock(x, y, z, AIR);
    if (h.getBlock(x, y, z) !== AIR) return; // refused
    this.spawn(id, x, y, z);
  }

  /** Put a block in flight from the cell (x, y, z), whose block the caller has already removed. */
  spawn(id: number, x: number, y: number, z: number): FallingBlock {
    const b: FallingBlock = { id, x: x + 0.5, y, z: z + 0.5, px: x + 0.5, py: y, pz: z + 0.5, vy: 0, age: 0, fall: 0 };
    this.blocks.push(b);
    return b;
  }

  /** Vanilla FallingBlockEntity.tick. Returns true when the block is done (placed, dropped or gone). */
  private fly(b: FallingBlock): boolean {
    b.px = b.x; b.py = b.y; b.pz = b.z;
    b.age++;
    b.vy -= GRAVITY;
    const box = [b.x - HALF_W, b.y, b.z - HALF_W, b.x + HALF_W, b.y + HEIGHT, b.z + HALF_W];
    const moved = clipMove(box, 1, b.vy, EPS, this.shapesAt);
    const onGround = moved !== b.vy && b.vy < 0;
    b.y += moved;
    if (moved < 0) b.fall -= moved;
    b.vy *= DRAG;
    if (!onGround) {
      const cy = Math.floor(b.y);
      if (b.age > MAX_AGE || (b.age > OUT_OF_WORLD_AGE && (cy <= 0 || cy > WORLD_HEIGHT))) {
        if (b.y > -1 && b.y < WORLD_HEIGHT + 1) this.host.drop?.(dropIdOf(b.id), b.x, Math.max(b.y, 0), b.z);
        return true;
      }
      return false;
    }
    this.land(b);
    return true;
  }

  private land(b: FallingBlock): void {
    const h = this.host;
    const bx = Math.floor(b.x), by = Math.floor(b.y + EPS), bz = Math.floor(b.z);
    let id = b.id;
    let cancelDrop = false;
    const anvil = anvilState(id);
    if (anvil) {
      const dmg = anvilDamage(b.fall);
      const box = [b.x - HALF_W, b.y, b.z - HALF_W, b.x + HALF_W, b.y + HEIGHT, b.z + HALF_W];
      if (dmg > 0) h.hurt?.(box, dmg, id);
      const n = Math.ceil(b.fall - 1 - EPS);
      if (dmg > 0 && (h.rnd ?? Math.random)() < 0.05 + n * 0.05) {
        if (anvil[0] >= 2) cancelDrop = true; // a damaged anvil is destroyed
        else id = ANVIL_FAMILIES[anvil[0] + 1][anvil[1]];
      }
    }
    if (cancelDrop) {
      h.fx?.('anvil_break', b.x, b.y, b.z, b.id);
      return;
    }
    const here = h.getBlock(bx, by, bz);
    const rests = by <= 0 || !isFree(h.getBlock(bx, by - 1, bz));
    if (isReplaceable(here) && rests && h.canEdit(bx, by, bz)) {
      h.setBlock(bx, by, bz, id);
      h.fx?.(anvil ? 'anvil_land' : 'land', bx, by, bz, id);
    } else {
      h.drop?.(dropIdOf(id), bx + 0.5, by + 0.25, bz + 0.5);
      h.fx?.('break', bx, by, bz, id);
    }
  }

  private readonly shapesAt: ShapesAt = (x, y, z) =>
    y < 0 || y >= WORLD_HEIGHT ? NO_BOXES : collisionOf(this.host.getBlock(x, y, z));

  // ---- persistence ---------------------------------------------------------

  /** What a save needs: blocks in the air as [id, x, y, z, vy, age, fall] and the checks still waiting as [x, y, z]. */
  serialize(): { b: number[][]; d: number[][] } {
    const r = (v: number): number => Math.round(v * 1e4) / 1e4;
    return {
      b: this.blocks.map((f) => [f.id, r(f.x), r(f.y), r(f.z), r(f.vy), f.age, r(f.fall)]),
      d: [...this.due.values()].map((c) => [c.x, c.y, c.z]),
    };
  }

  /** Replace the state with a saved one (anything malformed is skipped). */
  load(data: unknown): void {
    this.blocks.length = 0;
    this.due.clear();
    if (!data || typeof data !== 'object') return;
    const { b, d } = data as { b?: unknown; d?: unknown };
    const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    if (Array.isArray(b)) {
      for (const row of b) {
        if (!Array.isArray(row)) continue;
        const [id, x, y, z, vy, age, fall] = row.map(num);
        if (id === null || x === null || y === null || z === null || IS_GRAVITY[id] !== 1) continue;
        this.blocks.push({ id, x, y, z, px: x, py: y, pz: z, vy: vy ?? 0, age: Math.max(0, age ?? 0), fall: Math.max(0, fall ?? 0) });
      }
    }
    if (Array.isArray(d)) {
      for (const row of d) {
        if (!Array.isArray(row)) continue;
        const [x, y, z] = row.map(num);
        if (x !== null && y !== null && z !== null) this.schedule(Math.floor(x), Math.floor(y), Math.floor(z));
      }
    }
  }
}
